import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Check, Clear } from "@mui/icons-material";
import {
  CircularProgress,
  ClickAwayListener,
  Grow,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";

import Button from "../CustomButtons/Button";
import CustomInput from "../CustomInput/CustomInput";

import { match, parse } from "../../Helpers/AutosuggestHighlight";

import { withStyles } from "tss-react/mui";
import AutocompleteStyle from "./AutocompleteStyle";
import Input from "@mui/material/Input";

let typingTime = null;
let currentRequest;

function Autocomplete({ ...props }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(props.data);
  const [inputText, setInputText] = useState("");
  const [value, setValue] = useState(props.value);
  const [loading, setLoading] = useState(false);
  const [hasSelectedItem, setHasSelectedItem] = useState(false);
  const [selectedData, setSelectedData] = useState();
  const popperAnchor = useRef(null);

  const { classes, hoverColor, dropPlacement, noLiPadding, innerDropDown, navDropdown } = props;

  /**
   * Component did mount
   */
  useEffect(() => {
    if (value !== undefined && value !== null) {
      if (props.async) {
        let data;

        /**
         * A sync autocomplete
         */
        if (props.multiple) {
          data = [
            {
              field: "id",
              operator: "in",
              value: value,
            },
          ];
        } else {
          data = [
            {
              field: "id",
              operator: "=",
              value: value,
            },
          ];
        }

        axios.post(props.async.url, data).then((response) => {
          setData(response.data.data.data);
          setHasSelectedItem(true);

          if (props.multiple) {
            setSelectedData(response.data.data.data);
          } else {
            setInputText(response.data.data.data[0].label);
            setValue(response.data.data.data[0].value);
          }
        });
      } else {
        /**
         * Normal autocomplete
         */
        let selected = data.filter((item) => {
          return item.value === value;
        });

        if (selected.length >= 1) {
          setInputText(selected[0].label);
          setValue(selected[0].value);
          setHasSelectedItem(true);
        }
      }
    }
  }, []);

  /**
   * Component unmount
   */
  useEffect(
    () => () => {
      cancelRequest();
      clearTimeout(typingTime);
    },
    []
  );

  const caretClasses = classNames({
    [classes.caret]: true,
    [classes.caretActive]: open,
  });

  const dropdownItem = classNames({
    [classes.dropdownItem]: true,
    [classes[hoverColor + "Hover"]]: true,
    [classes.noLiPadding]: noLiPadding,
  });

  /**
   * Cancel the axios request
   */
  const cancelRequest = () => {
    if (currentRequest !== undefined) {
      currentRequest.cancel();
    }
  };

  /**
   * Handle input change
   */
  const handleChangeInput = (input) => {
    if (!props.multiple) {
      setValue(null);
    }

    setHasSelectedItem(false);
    setInputText(input);
    setOpen(true);

    if (props.async) {
      getRemoteData(input);
    }
  };

  /**
   * Bold maken van de query in de output
   */
  const partsRender = (text) => {
    const matches = match(text, inputText);
    const parts = parse(text, matches);

    return (
      <div className={classes.parts}>
        {parts.map((part, index) => (
          <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
            {part.text}
          </span>
        ))}
      </div>
    );
  };

  /**
   * Handle click item
   */
  const handleItemClick = (item) => {
    cancelRequest();

    setLoading(false);
    setHasSelectedItem(true);

    if (!props.multiple) {
      setOpen(false);
      setInputText(item.label);
    }

    if (props.multiple) {
      if (value === undefined || value === null) {
        setValue([item.value]);
        setSelectedData([item]);
        handlePropsOnClick([item.value]);
      } else {
        if (value.includes(item.value)) {
          let newValue = value.filter((selectedValue) => {
            return selectedValue !== item.value;
          });
          setValue([...newValue]);
          handlePropsOnClick(newValue);
          let newSelectedData = selectedData.filter((selectedItem) => {
            return selectedItem.value !== item.value;
          });

          setSelectedData([...newSelectedData]);
        } else {
          setValue([...value, item.value]);
          setSelectedData([...selectedData, item]);
          handlePropsOnClick([...value, item.value]);
        }
      }
    } else {
      setValue(item);
      handlePropsOnClick(item);
    }
  };

  /**
   * Handle props on Click
   */
  const handlePropsOnClick = (output) => {
    if (props && props.onClick) {
      props.onClick(output);
    }
  };

  /**
   * Reset click (rood kruis)
   */
  const handleResetClick = () => {
    cancelRequest();

    setInputText("");
    setOpen(false);
    setLoading(false);
    setHasSelectedItem(false);
    setValue(null);
    setSelectedData(null);

    if (props.multiple) {
      handlePropsOnClick([]);
    } else {
      handlePropsOnClick({
        value: null,
        label: null,
      });
    }
  };

  /**
   * Handlekey press voor input
   * Als er nog maar 1 is dan kun je op enter klikken en dan selecteerd die die ook
   */
  const handleKeyPress = (key) => {
    if (key === "Enter") {
      if (getFilteredData().length === 1) {
        handleItemClick(getFilteredData()[0]);
      }
    }
  };

  /**
   * Get filtered data
   */
  const getFilteredData = () => {
    if (!data.filterDataUsingInput) {
      return data;
    }
    return data.filter((item) => {
      return inputText !== "" ? item.label.toLowerCase().includes(inputText.toLowerCase()) : true;
    });
  };

  /**
   * Selected item voor de dropdown
   */
  const isSelectedItem = (item) => {
    /**
     * Return false als nog niks is geselecteerd
     */
    if (value === undefined || value === null) {
      return false;
    }

    if (props.multiple) {
      return value.includes(item.value);
    } else {
      return item.value === value;
    }
  };

  /**
   * Render menuItem
   */
  const dropDownMenu = (
    <MenuList className={classes.menuList} role={"menu"}>
      {getFilteredData().map((prop, key) => {
        return (
          <MenuItem className={dropdownItem} key={key} onClick={() => handleItemClick(prop)} value={props.value}>
            {partsRender(prop.label)}
            {isSelectedItem(prop) ? <Check className={classes.selected} /> : null}
          </MenuItem>
        );
      })}
    </MenuList>
  );

  /**
   * Handle input click
   */
  const handleClick = (e) => {
    if (e.target.id === "searchinput") {
      let input = e.target.value;
      if (props.async) {
        getRemoteData(input);
      } else {
        setOpen(true);
      }
    }
  };

  /**
   * Get remote data for async request
   */
  const getRemoteData = (input) => {
    setLoading(true);

    let data = [
      {
        field: props.async.searchField,
        operator: "like",
        value: "%" + input + "%",
      },
    ];

    if (props.async.extraFilters) {
      data = [...data, ...props.async.extraFilters];
    }

    /**
     * Reset typing timer
     */
    if (typingTime !== null) {
      clearTimeout(typingTime);
    }

    cancelRequest();

    const CancelToken = axios.CancelToken;
    currentRequest = CancelToken.source();
    let cancelToken = currentRequest.token;

    typingTime = setTimeout(() => {
      axios
        .post(props.async.url, data, {
          cancelToken: cancelToken,
        })
        .then((response) => {
          setData(response.data.data.data);
          setLoading(false);
          setOpen(true);
        });
    }, 500);
  };

  /**
   * Render multiple selected
   */
  const getMultipleSelectedTags = () => {
    if (selectedData === undefined || selectedData === null) {
      return null;
    } else {
      let first3Items = selectedData.slice(0, 3).map((item, key) => {
        return (
          <Button className={classes.multipleSelect} color={"primary"} key={key} round size={"sm"}>
            {item.label}
            <Clear className={classes.multipleSelectDelete} onClick={() => handleItemClick(item)} />
          </Button>
        );
      });

      let extraItemsCounter = [];

      if (selectedData.length > 3) {
        extraItemsCounter = [
          <Button className={classes.multipleSelectMoreItems} color={"primary"} round size={"sm"}>
            + {selectedData.length - 3}
          </Button>,
        ];
      }

      return [...first3Items, ...extraItemsCounter];
    }
  };

  /**
   * Render het component
   */
  return (
    <div className={classes.manager}>
      <div className={classes.target} ref={popperAnchor}>
        {props.hiddenFieldName ? (
          props.multiple ? (
            selectedData?.map((item) => (
              <Input
                id={props.hiddenFieldName}
                key={item.value}
                name={props.hiddenFieldName}
                type={"hidden"}
                value={item.value}
              />
            ))
          ) : (
            <Input id={props.hiddenFieldName} name={props.hiddenFieldName} type={"hidden"} value={value?.value} />
          )
        ) : null}
        <CustomInput
          formControlProps={{
            className: classes.input,
          }}
          id={"searchinput"}
          inputProps={{
            autoComplete: "off",
            placeholder: props.placeHolderText,
            value: inputText,
            onChange: (e) => handleChangeInput(e.target.value),
            onKeyPress: (e) => handleKeyPress(e.key),
            onClick: (e) => handleClick(e),
            startAdornment: props.multiple ? (
              <InputAdornment position={"start"}>{getMultipleSelectedTags()}</InputAdornment>
            ) : null,
            endAdornment: (
              <InputAdornment position={"end"}>
                {loading ? <CircularProgress className={classes.loading} color={"inherit"} size={20} /> : null}
                {hasSelectedItem ? (
                  <Clear className={classes.clear} onClick={handleResetClick} size={15} style={{ color: "red" }} />
                ) : null}
                <b className={caretClasses} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Popper
        anchorEl={popperAnchor.current}
        className={classNames({
          [classes.popperClose]: !open,
          [classes.popperResponsive]: true,
          [classes.popperNav]: open && navDropdown,
        })}
        disablePortal={props.disablePortal}
        open={open}
        placement={dropPlacement}
        popperOptions={{
          strategy: "absolute",
        }}
        transition
      >
        {() => (
          <Grow id={"menu-list"} in={open} style={{ transformOrigin: "0 0 0" }}>
            <Paper className={classes.dropdown}>
              {innerDropDown ? (
                dropDownMenu
              ) : (
                <ClickAwayListener onClickAway={() => setOpen(false)}>{dropDownMenu}</ClickAwayListener>
              )}
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}

Autocomplete.defaultProps = {
  placeHolderText: "Selecteer een vestiging",
  async: false,
  multiple: false,
  data: [],
  dropPlacement: "bottom-start",
  disablePortal: true,
  filterDataUsingInput: false,
};

Autocomplete.propTypes = {
  placeHolderText: PropTypes.string,
  async: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  data: PropTypes.array,
  multiple: PropTypes.bool,
  dropPlacement: PropTypes.string,
  onClick: PropTypes.func,
  hiddenFieldName: PropTypes.string,
  disablePortal: PropTypes.bool,
  filterDataUsingInput: PropTypes.bool,
};

export default withStyles(Autocomplete, AutocompleteStyle);
